import config from "../data/config.json";
import { emptyToNull, isValueNull } from "@1823-partners/core";

// Re-export isValueNull from core for existing consumers
export { isValueNull };

/**
 * Parse an AG Grid numeric cell edit. After a successful edit, Shift-Tab /
 * stopEditing can re-submit `newValue` as a number (not a string), so callers
 * must not assume `.replace` exists.
 *
 * @param {*} newValue - editor value (string, number, null, or empty)
 * @param {{ percent?: boolean }} [options] - when percent, string inputs like
 *   "4.5" / "4.5%" are stored as 0.045; already-parsed numbers are kept as-is
 * @returns {number|null}
 */
export const parseNumericCellValue = (newValue, { percent = false } = {}) => {
  if (newValue === '' || newValue == null) return null;
  if (typeof newValue === 'number') {
    return Number.isNaN(newValue) ? null : newValue;
  }
  const cleaned = String(newValue).replace(/,/g, '').replace(/%/g, '').trim();
  if (cleaned === '') return null;
  const parsed = Number(cleaned);
  return percent ? parsed / 100 : parsed;
};

/**
 * Formats entry data for submission to the API
 * @param {Object} entry - The entry data to format
 * @returns {Object} - The formatted entry data
 */
export const formatEntryData = (entry) => {
  const formattedEntry = { ...entry };

  // Remove the actions column
  delete formattedEntry.actions;

  // Calculate Est Proceeds
  const currentFace = formattedEntry.CURRENT_FACE;
  const price = formattedEntry.PRICE;
  const direction = formattedEntry.DIRECTION;
  if (!isValueNull(price) && !isValueNull(currentFace) && !isValueNull(direction)) {
    const multiplier = direction.toUpperCase() === 'BUY' ? 1 : -1;
    formattedEntry.EST_PROCEEDS = (price * currentFace / 100) * multiplier;
  }

  // Calculate Thickness
  const currCreditSupport = formattedEntry.CURR_CREDIT_SUPPORT;
  const ssfaDetachmentPoint = formattedEntry.SSFA_DETACHMENT_POINT;
  if (!isValueNull(currCreditSupport) && !isValueNull(ssfaDetachmentPoint)) {
    formattedEntry.THICKNESS = ssfaDetachmentPoint - currCreditSupport;
  }

  // Convert boolean fields from 'Y'/'N' strings to true/false/null
  formattedEntry.ILLIQUID = formattedEntry.ILLIQUID === 'Y' ? true : formattedEntry.ILLIQUID === 'N' ? false : null;
  formattedEntry.FLOATER = formattedEntry.FLOATER === 'Y' ? true : formattedEntry.FLOATER === 'N' ? false : null;
  formattedEntry.OVERRIDE = formattedEntry.OVERRIDE === 'Y' ? true : formattedEntry.OVERRIDE === 'N' ? false : null;

  // Convert "N/A" risk retention to null for backend storage
  if (formattedEntry.RISK_RETENTION === 'N/A') {
    formattedEntry.RISK_RETENTION = null;
  }

  // Convert remaining empty fields to null using 1823-core utility
  return emptyToNull(formattedEntry);
};

/**
 * Validates entry data against required fields
 * @param {Object} entryData - The entry data to validate
 * @param {boolean} isNewEntry - Whether this is a new entry (affects BLOTTER_ID validation)
 * @returns {Array} - Array of missing required field names
 */
export const validateEntryData = (entryData, isNewEntry = false) => {
  const emptyFields = config.validation.requiredFields.filter(field => {
    // Special handling for BLOTTER_ID - if it's a new entry, it's OK to be empty
    if (field === 'BLOTTER_ID' && isNewEntry) {
      return false;
    }
    return isValueNull(entryData[field]);
  });

  return emptyFields;
};

/**
 * Validates a list of entries and returns validation errors
 * @param {Array} entries - Array of entry objects to validate
 * @returns {Array} - Array of validation error objects with row, cusip, description, and emptyFields
 */
export const validateEntryList = (entries) => {
  const errorEntries = [];
  entries.forEach((entry, idx) => {
    const emptyFields = config.validation.requiredFields.filter(col => isValueNull(entry[col]));
    if (emptyFields.length !== 0) {
      errorEntries.push({
        row: idx,
        cusip: entry['CUSIP'],
        description: entry['DESCRIPTION'],
        emptyFields: emptyFields
      });
    }
  });

  return errorEntries;
};

/**
 * Converts blotter validation errors to 1823-core ValidationErrorModal format
 * @param {Array} errors - Array of validation error objects from validateEntryList
 * @param {Object} fieldToHeaderMap - Optional mapping of field names to display names
 * @returns {Array} - Array of formatted error strings for 1823-core ValidationErrorModal
 */
export const formatValidationErrors = (errors, fieldToHeaderMap = {}) => {
  const getFieldDisplayName = (fieldName) => fieldToHeaderMap[fieldName] || fieldName;

  return errors.flatMap(error => {
    const rowLabel = `Row ${error.row + 1}`;
    const identifier = error.cusip || error.description || 'Entry';

    if (error.errorMessages && error.errorMessages.length > 0) {
      return error.errorMessages.map(errMsg =>
        `${rowLabel}: ${identifier} - ${getFieldDisplayName(errMsg.field)}: ${errMsg.message}`
      );
    }

    if (error.emptyFields && error.emptyFields.length > 0) {
      return error.emptyFields.map(field =>
        `${rowLabel}: ${identifier} - Missing ${getFieldDisplayName(field)}`
      );
    }

    return [];
  });
}; 
/** True for a row id the grid made up locally (`new_row_<n>`) or none at all. */
export const isPlaceholderEntryId = (id) =>
  id === null || id === undefined || id === '' || (typeof id === 'string' && id.startsWith('new_row_'));

/**
 * A fresh BLOTTER_ID for a never-saved entry (RFC 4122 v4).
 *
 * The browser picks it once per new row and keeps it across retries, so a
 * retried save whose first attempt did land is a 409 at the writer (the key
 * now has a row) instead of a second copy of the entry.
 */
export const newEntryId = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // eslint-disable-line no-bitwise
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // eslint-disable-line no-bitwise
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
